// ParticleBackground.jsx
import { useEffect, useRef } from "react";
import "./particleBackground.css";

// Цвета частиц для каждой темы
const THEME_COLORS = {
  dark:  { line: "#a5b4fc", particle: "#e0e7ff" },
  light: { line: "#6366f1", particle: "#4f46e5" },
};

// Линейная интерполяция числа
const lerp = (a, b, t) => a + (b - a) * t;

// Интерполяция hex-цвета между двумя значениями (возвращает hex)
const hexToRgb = (hex) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgbToHex = ([r, g, b]) =>
  '#' + [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('');

const lerpColor = (hexA, hexB, t) =>
  rgbToHex(hexToRgb(hexA).map((a, i) => lerp(a, hexToRgb(hexB)[i], t)));

export default function ParticleBackground({ theme = "dark" }) {
  const containerRef = useRef(null);

  // themeRef хранит текущую тему для анимационного цикла без пересоздания canvas
  const themeRef = useRef(theme);

  // colorRef хранит текущие интерполированные цвета (обновляются каждый кадр)
  const colorRef = useRef({ ...THEME_COLORS[theme] });

  // ── Эффект 1: создаём canvas один раз ──────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const canvas = document.createElement("canvas");
    container.appendChild(canvas);
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
    };
    resize();

    const options = {
      velocity: 0.3,
      density: 8000,
      netLineDistance: 200,
      lineWidth: 0.8,
    };

    let particles = [];
    let interactionParticle = null;

    const createParticles = () => {
      const quantity = (canvas.width * canvas.height) / options.density;
      particles = [];
      for (let i = 0; i < quantity; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * options.velocity,
          vy: (Math.random() - 0.5) * options.velocity,
          r: 1.5,
          opacity: 0.8 + Math.random() * 0.2,
        });
      }
    };
    createParticles();

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      if (!interactionParticle) {
        interactionParticle = { x: mouseX, y: mouseY, vx: 0, vy: 0, r: 4, opacity: 1, isInteraction: true };
        particles.push(interactionParticle);
      } else {
        interactionParticle.x = mouseX;
        interactionParticle.y = mouseY;
      }
    };
    const onMouseLeave = () => {
      if (interactionParticle) {
        particles = particles.filter(p => p !== interactionParticle);
        interactionParticle = null;
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("resize", resize);

    let animationFrame;

    const animate = () => {
      // ── Плавная интерполяция цветов ──────────────────────────────────────
      // Скорость перехода: ~0.025 за кадр при 60fps ≈ ~700ms
      const LERP_SPEED = 0.025;
      const target = THEME_COLORS[themeRef.current] || THEME_COLORS.dark;
      const cur = colorRef.current;

      cur.line     = lerpColor(cur.line,     target.line,     LERP_SPEED);
      cur.particle = lerpColor(cur.particle, target.particle, LERP_SPEED);
      // ─────────────────────────────────────────────────────────────────────

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < options.netLineDistance) {
            ctx.beginPath();
            ctx.strokeStyle = cur.line;
            ctx.globalAlpha =
              (1 - dist / options.netLineDistance) ** 2 * p1.opacity * p2.opacity * 0.8;
            ctx.lineWidth = options.lineWidth;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;

      particles.forEach(p => {
        if (!p.isInteraction) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < -100 || p.x > canvas.width + 100) p.vx *= -1;
          if (p.y < -100 || p.y > canvas.height + 100) p.vy *= -1;
        }
        ctx.beginPath();
        ctx.fillStyle = cur.particle;
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrame = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("resize", resize);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
  }, []); // ← пустой массив: canvas создаётся только один раз

  // ── Эффект 2: при смене theme обновляем только ref ────────────────────────
  // Анимационный цикл сам считывает themeRef и плавно переходит к новым цветам
  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  return <div className={`particle-network-animation ${theme}`} ref={containerRef} />;
}